import React from 'react';
import { Platform, StyleSheet } from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import {
  BodyText,
  theme,
  useContainerSpacing,
  VStack,
  webOnlyStyles,
} from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useCustomer } from '@order/Customer';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { getAppVersion } from '@order/utils';

import { useKustomerAutoStart } from '../../../integrations/kustomer';
import type { AccountTabScreenId } from '../../../navigation/AppNavigation.props';
import { resetStacksPostLogout } from '../../../screens/AccountScreen/AccountScreen.navigation';
import { AccountSignedOutMessage } from '../AccountSignedOutMessage';
import type { AccountMenuVariant } from './AccountMenu.types';
import {
  AccountCustomerName,
  AccountMenuCrashOptions,
  AccountMenuIconRow,
  AccountMenuSignOutItem,
  AccountPrimaryMenu,
  AccountSecondaryMenu,
} from './components';

export const AccountMenu = ({
  variant,
  screen: screenId,
}: AccountMenuProps) => {
  const { isSigningOut, signOut } = useCustomer();
  const isLoggedIn = useIsLoggedIn();
  const testID =
    variant === 'STANDALONE'
      ? 'account-menu-standalone'
      : 'account-menu-sidebar';

  // ─── Render Control ──────────────────────────────────────────────
  const isLoggedOutMessageVisible = !isLoggedIn;
  const isCustomerNameVisible = isLoggedIn && variant === 'STANDALONE';
  const isPrimaryMenuVisible = isLoggedIn;
  const isSecondaryMenuVisible =
    Platform.OS !== 'web' && variant === 'STANDALONE';
  const isIconRowVisible = isSecondaryMenuVisible;
  const isSignOutItemVisible = isLoggedIn;
  const isCrashMenuEnabled = useFeatureFlag(
    'permanent-order-crash-option-enabled',
  );

  // ─── Callbacks ───────────────────────────────────────────────────────
  const signOutAndReset = React.useCallback(async () => {
    await signOut();
    resetStacksPostLogout();
  }, [signOut]);

  // ─── Effects ─────────────────────────────────────────────────────────
  useKustomerAutoStart();

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerSpacing = useContainerSpacing();

  const menuContainerDynamicStyle = useStyle(
    () => ({ paddingHorizontal: containerSpacing }),
    [containerSpacing],
  );
  const menuContainerStyle = [
    styles.sidebarMenuContainer,
    menuContainerDynamicStyle,
    menuContainerWebStyle,
  ];
  const standaloneContainerStyle = styles.standaloneContainer;

  // ─────────────────────────────────────────────────────────────────────

  return (
    <VStack
      testID={testID}
      style={
        variant === 'STANDALONE' ? standaloneContainerStyle : menuContainerStyle
      }
      customSpacing={0}
    >
      {isLoggedOutMessageVisible ? <AccountSignedOutMessage /> : null}

      {isCustomerNameVisible ? <AccountCustomerName /> : null}

      {isPrimaryMenuVisible ? (
        <AccountPrimaryMenu
          variant={variant}
          screenId={screenId}
          isSecondaryMenuVisible={isSecondaryMenuVisible}
        />
      ) : null}

      {isSecondaryMenuVisible ? (
        <AccountSecondaryMenu variant={variant} />
      ) : null}

      {isCrashMenuEnabled ? <AccountMenuCrashOptions /> : null}

      {isIconRowVisible ? <AccountMenuIconRow /> : null}

      {isSignOutItemVisible ? (
        <AccountMenuSignOutItem
          variant={variant}
          isSigningOut={isSigningOut}
          onSignOut={signOutAndReset}
        />
      ) : null}

      <BodyText style={styles.appVersionText}>v{getAppVersion()}</BodyText>
    </VStack>
  );
};

type AccountMenuProps = {
  readonly variant: AccountMenuVariant;
  readonly screen?: AccountTabScreenId;
};

const styles = StyleSheet.create({
  standaloneContainer: {
    marginRight: theme.spacing.xlarge - theme.spacing.small,
    width: '100%',
  },
  sidebarMenuContainer: {
    paddingVertical: 32,
  },
  appVersionText: {
    marginTop: theme.spacing.large,
  },
});

const menuContainerWebStyle = webOnlyStyles({
  position: 'sticky',
  top: 0,
} as unknown as Parameters<typeof webOnlyStyles>[0]);
